import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogOverlay,
  Flex,
  HStack,
  IconButtonProps,
  Text,
  VStack,
} from '@chakra-ui/react'
import { PopUpMessageContext } from '@hb/react'
import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { ActionButton, SolidActionButton } from './ActionButton'

const defaultAlertBodyText = "Are you sure? You can't undo this action afterwards."
export const ConfirmAlert = ({
  confirmActive,
  onCancel,
  onConfirm,
  body = defaultAlertBodyText,
  disabled,
  actionName = 'Confirm',
  actionLoading,
  itemName,
}: {
  confirmActive: boolean
  onCancel: () => void
  onConfirm: () => void
  disabled?: boolean
  body?: string | ReactNode
  actionLoading?: boolean
  actionName?: string
  itemName: string
}) => {
  const cancelRef = useRef<HTMLButtonElement>(null)
  return (
    <AlertDialog
      isCentered
      isOpen={confirmActive}
      leastDestructiveRef={cancelRef}
      onClose={() => onCancel()}>
      <AlertDialogOverlay>
        <AlertDialogContent bg="gray.50" gap={0}>
          <AlertDialogBody px={4} py={2}>
            <VStack align="flex-start" py={1}>
              <Text fontFamily="Hero-New" fontSize="lg" fontWeight={500}>
                {actionName} {itemName}?
              </Text>
              {typeof body === 'string' ? (
                <Text fontFamily="Hero-New" fontSize="md">
                  {body}
                </Text>
              ) : (
                body
              )}
              <HStack justify="flex-end" w="100%">
                <ActionButton size="sm" ref={cancelRef} onClick={onCancel}>
                  Cancel
                </ActionButton>
                <SolidActionButton
                  size="sm"
                  isDisabled={disabled || actionLoading}
                  isLoading={actionLoading}
                  onClick={onConfirm}>
                  {actionName}
                </SolidActionButton>
              </HStack>
            </VStack>
          </AlertDialogBody>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}

export const ConfirmAlertButton: React.FC<
  Partial<IconButtonProps> & {
    onConfirm: (() => Promise<any>) | (() => void)
    itemName: string
    actionName: string
    disabled?: boolean
    noConfirm?: boolean
    text?: string
    alertBody?: string | ReactNode
  }
> = ({
  onConfirm,
  itemName,
  noConfirm,
  alertBody,
  text,
  actionName = 'Confirm',
  icon,
  disabled = false,
  ...props
}) => {
  const [confirmLoading, setConfirmLoading] = useState(false)
  const [confirmActive, setConfirmActive] = useState(false)
  const confirmTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const isMounted = useRef(true)
  useEffect(() => {
    isMounted.current = true

    return () => {
      isMounted.current = false
    }
  }, [])

  const { showError } = useContext(PopUpMessageContext)

  const handleConfirm = useCallback(async () => {
    if (confirmTimer.current) clearTimeout(confirmTimer.current)
    setConfirmActive(false)
    if (onConfirm) {
      setConfirmLoading(true)
      try {
        await onConfirm()
      } catch (err: any) {
        console.error(err)
        showError(`Error confirming,${err?.message || 'Unknown error'}`)
        console.error(err)
      }
      if (isMounted.current) {
        setConfirmLoading(false)
      }
    } else {
      console.error('No confirm function')
    }
  }, [onConfirm, showError])
  const displayedText = useMemo(
    () => text ?? `${actionName} ${itemName}`,
    [text, actionName, itemName],
  )

  return (
    <>
      <SolidActionButton
        variant="ghost"
        size={props.size || 'md'}
        aria-label="confirm"
        onClick={e => {
          e.stopPropagation()
          if (noConfirm) handleConfirm()
          else setConfirmActive(true)
        }}
        isLoading={confirmLoading}
        {...props}>
        <Flex align="center" gap={1}>
          {icon ?? null}
          <Text position="relative" top="1px">
            {displayedText}
          </Text>
        </Flex>
      </SolidActionButton>
      <ConfirmAlert
        confirmActive={confirmActive}
        onCancel={() => {
          setConfirmActive(false)
        }}
        disabled={disabled}
        actionLoading={confirmLoading}
        onConfirm={handleConfirm}
        actionName={actionName}
        body={alertBody}
        itemName={itemName}
      />
    </>
  )
}
